// Sistema de temas para Playa Niklitschek - VERSIÓN MEJORADA 2025
// Paletas profesionales basadas en el logo con colores naturales y accesibles

export type ThemeName = 'tema1' | 'tema2' | 'tema3' | 'tema4' | 'tema5'

export interface ThemeColors {
  primary: string
  primaryDark: string
  primaryLight: string
  secondary: string
  accent: string
  background: string
  surface: string
  text: string
  textSecondary: string
  border: string
  success: string
  warning: string
  error: string
}

export const themes: Record<ThemeName, ThemeColors> = {
  // ========================================================================
  // Tema 1: AZUL LAGO NATURAL (Recomendado para camping/naturaleza)
  // Inspirado en el agua del lago - Azules naturales y suaves
  // ========================================================================
  tema1: {
    primary: '#1e40af',       // Azul principal natural (no eléctrico)
    primaryDark: '#1e3a8a',   // Azul oscuro profundo
    primaryLight: '#3b82f6',  // Azul lago claro
    secondary: '#84cc16',     // Verde naturaleza (del logo)
    accent: '#0ea5e9',        // Celeste agua
    background: '#f8fafc',    // Gris muy claro
    surface: '#FFFFFF',       // Blanco puro
    text: '#1a365d',          // Azul marino para texto principal
    textSecondary: '#64748b', // Gris medio (contraste 7:1)
    border: '#e2e8f0',        // Gris borde suave
    success: '#84cc16',       // Verde naturaleza
    warning: '#f59e0b',       // Ámbar
    error: '#dc2626',         // Rojo estándar
  },

  // ========================================================================
  // Tema 2: VERDE BOSQUE (Para conexión con naturaleza)
  // Dominado por verdes y tierra - Muy orgánico
  // ========================================================================
  tema2: {
    primary: '#16a34a',       // Verde bosque principal
    primaryDark: '#15803d',   // Verde oscuro
    primaryLight: '#22c55e',  // Verde brillante
    secondary: '#3b82f6',     // Azul lago de apoyo
    accent: '#84cc16',        // Verde lima acento
    background: '#f0fdf4',    // Verde muy claro
    surface: '#FFFFFF',       // Blanco puro
    text: '#14532d',          // Verde muy oscuro para texto
    textSecondary: '#64748b', // Gris medio
    border: '#dcfce7',        // Verde borde claro
    success: '#16a34a',       // Verde principal
    warning: '#f59e0b',       // Ámbar
    error: '#dc2626',         // Rojo estándar
  },

  // ========================================================================
  // Tema 3: TIERRA Y AGUA (Combinación equilibrada)
  // Balance entre azul lago y tonos tierra
  // ========================================================================
  tema3: {
    primary: '#0284c7',       // Azul cielo/agua
    primaryDark: '#0369a1',   // Azul profundo
    primaryLight: '#0ea5e9',  // Celeste brillante
    secondary: '#f97316',     // Naranja tierra/atardecer
    accent: '#84cc16',        // Verde acento
    background: '#f0f9ff',    // Azul muy claro
    surface: '#FFFFFF',       // Blanco puro
    text: '#0c4a6e',          // Azul oscuro para texto
    textSecondary: '#64748b', // Gris medio
    border: '#e0f2fe',        // Azul borde claro
    success: '#84cc16',       // Verde
    warning: '#f97316',       // Naranja
    error: '#dc2626',         // Rojo estándar
  },

  // ========================================================================
  // Tema 4: LOGO ORIGINAL MEJORADO (Colores del logo optimizados)
  // Usa los colores exactos del logo pero con mejor accesibilidad
  // ========================================================================
  tema4: {
    primary: '#0ea5e9',       // Celeste brillante (similar a #14C7FA pero más accesible)
    primaryDark: '#1e40af',   // Azul oscuro mejorado (similar a #060A91)
    primaryLight: '#38bdf8',  // Celeste claro
    secondary: '#84cc16',     // Verde del logo
    accent: '#65a30d',        // Verde oscuro
    background: '#f0f9ff',    // Azul muy claro
    surface: '#FFFFFF',       // Blanco puro
    text: '#1e40af',          // Azul oscuro para texto
    textSecondary: '#64748b', // Gris medio
    border: '#e0f2fe',        // Azul borde claro
    success: '#84cc16',       // Verde del logo
    warning: '#f59e0b',       // Ámbar
    error: '#dc2626',         // Rojo estándar
  },

  // ========================================================================
  // Tema 5: NIKLITSCHEK SIGNATURE (Premium - Propuesta Principal)
  // La paleta más equilibrada y profesional
  // Combina azules naturales del lago con verde naturaleza
  // ========================================================================
  tema5: {
    primary: '#1e40af',       // Azul principal profesional
    primaryDark: '#1e3a8a',   // Azul oscuro elegante
    primaryLight: '#3b82f6',  // Azul lago
    secondary: '#84cc16',     // Verde naturaleza
    accent: '#0ea5e9',        // Celeste agua
    background: '#ffffff',    // Blanco puro
    surface: '#f8fafc',       // Gris muy claro
    text: '#1a365d',          // Azul marino texto
    textSecondary: '#64748b', // Gris medio perfecto
    border: '#e2e8f0',        // Gris borde
    success: '#84cc16',       // Verde
    warning: '#f97316',       // Naranja atardecer
    error: '#dc2626',         // Rojo estándar
  },
}

export const themeLabels: Record<ThemeName, string> = {
  tema1: 'Azul Lago Natural',
  tema2: 'Verde Bosque',
  tema3: 'Tierra y Agua',
  tema4: 'Logo Original',
  tema5: 'Niklitschek Signature ⭐',
}

export const themeDescriptions: Record<ThemeName, string> = {
  tema1: 'Azules naturales inspirados en el lago Llanquihue',
  tema2: 'Verdes orgánicos que conectan con la naturaleza',
  tema3: 'Balance perfecto entre agua y tierra',
  tema4: 'Colores del logo optimizados para web',
  tema5: 'Paleta premium equilibrada y profesional',
}

export const getTheme = (themeName: ThemeName): ThemeColors => {
  return themes[themeName] || themes.tema5 // Default a Signature
}

export const getAllThemeNames = (): ThemeName[] => {
  return Object.keys(themes) as ThemeName[]
}
